import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../../styles/UISystem';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { ALL_CONTACT_TYPES } from '../../../utils/contactTypeConfig';

/**
 * ContactTypePickerOverlay
 *
 * Centered modal overlay for selecting a contact type to add.
 * Shows available contact types with icons, organized by primary/secondary.
 *
 * @param {boolean} isOpen - Whether overlay is visible
 * @param {object} availableTypes - { primary: string[], secondary: string[] }
 * @param {function} onSelect - Called with the selected type
 * @param {function} onClose - Called when overlay should dismiss
 */
const ContactTypePickerOverlay = ({ isOpen, availableTypes, onSelect, onClose }) => {
  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      };
    }
  }, [isOpen]);

  // Handle type selection
  const handleSelect = (type) => {
    onSelect(type);
    onClose();
  };

  // Render a type row with icon and label
  const renderTypeRow = (type) => {
    const config = ALL_CONTACT_TYPES[type] || ALL_CONTACT_TYPES.custom;
    const IconComponent = config.Icon;

    return (
      <button
        key={type}
        onClick={() => handleSelect(type)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          width: '100%',
          padding: '12px 16px',
          border: 'none',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
          borderRadius: '8px',
        }}
      >
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: COLORS.LIGHT_GRAY,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: COLORS.DARK_GRAY,
            flexShrink: 0,
          }}
        >
          <IconComponent size={20} weight="regular" />
        </div>
        <span
          style={{
            fontSize: '16px',
            color: COLORS.DARK_GRAY,
            fontWeight: '400',
          }}
        >
          {config.label}
        </span>
      </button>
    );
  };

  const hasPrimary = availableTypes?.primary?.length > 0;
  const hasSecondary = availableTypes?.secondary?.length > 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            background: 'rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          {/* Centered modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '340px',
              maxHeight: '70vh',
              backgroundColor: 'white',
              borderRadius: BORDER_RADIUS.PILL,
              boxShadow: SHADOWS.TIER3_UP,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: '1px solid #E5E5E5',
              }}
            >
              <h2
                style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: COLORS.DARK_GRAY,
                  margin: 0,
                }}
              >
                Add Contact
              </h2>
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: COLORS.MEDIUM_GRAY,
                }}
                aria-label="Close"
              >
                <X size={24} weight="bold" />
              </button>
            </div>

            {/* Scrollable content */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '8px 4px',
              }}
            >
              {/* Primary types */}
              {hasPrimary && (
                <div style={{ marginBottom: hasSecondary ? '8px' : 0 }}>
                  {availableTypes.primary.map(renderTypeRow)}
                </div>
              )}

              {/* Divider */}
              {hasPrimary && hasSecondary && (
                <div
                  style={{
                    height: '1px',
                    backgroundColor: '#E5E5E5',
                    margin: '8px 16px',
                  }}
                />
              )}

              {/* Secondary types */}
              {hasSecondary && <div>{availableTypes.secondary.map(renderTypeRow)}</div>}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContactTypePickerOverlay;
